/**
 * Abgleich der lokalen Kopie einer Kampagne mit dem Server (Änderungsprotokoll, Phase 4).
 * Läuft beim Öffnen der Kampagne, nach Echtzeit-Ereignissen, nach Unterbrechungen und regelmäßig.
 */
import type { CampaignDTO, NoteDetail, SyncResponse } from '@kartothek/shared';
import type { QueryClient } from '@tanstack/react-query';
import { cachedUrls, campaignPrefix, combatUrl, deleteCached, isCacheable, mapUrl, noteUrl, readCached, writeCached, writeManyCached } from './cache';
import { idbGet, idbPut } from './idb';
import { flushOutbox } from './outbox';
import { getOfflineState, isUnreachableStatus, reportOffline, reportOnline, setOfflineState } from './state';

let queryClient: QueryClient | null = null;
export function setSyncQueryClient(qc: QueryClient): void {
  queryClient = qc;
}

const DETAIL = /\/(notes|maps|combats)\/[0-9a-f-]{36}$/i;
/** Listen, die beim ersten Abgleich immer mitkommen. */
const BASE_LISTS = ['/notes', '/types', '/graph', '/hierarchy', '/open-links', '/combats', '/files', '/catalog', '/items', '/screen', '/types/quest/notes', '/types/session/notes', '/types/charakter/notes'];
/** Nur für DMs/Co-DMs – Spieler bekämen 403. */
const GM_LISTS = new Set(['/open-links', '/combats', '/files', '/catalog', '/items', '/screen']);

const running = new Map<string, Promise<void>>();
const again = new Set<string>();

/** Abgleich anstoßen; läuft schon einer, folgt direkt danach ein weiterer. */
export function pullCampaign(cid: string): Promise<void> {
  const current = running.get(cid);
  if (current) {
    again.add(cid);
    return current;
  }
  const p = (async () => {
    try {
      do {
        again.delete(cid);
        await pullOnce(cid);
      } while (again.has(cid));
    } finally {
      running.delete(cid);
    }
  })();
  running.set(cid, p);
  return p;
}

async function getJson<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'kartothek' } });
    if (isUnreachableStatus(res.status)) {
      reportOffline();
      return null;
    }
    if (!res.ok) return null;
    const data = (await res.json()) as T;
    if (isCacheable(url)) await writeCached(url, data);
    return data;
  } catch {
    reportOffline();
    return null;
  }
}

async function pullOnce(cid: string): Promise<void> {
  if (!getOfflineState().online) return;
  // Erst eigene gepufferte Änderungen senden, sonst überschreibt der Server-Stand die lokalen
  if (getOfflineState().pending > 0) {
    await flushOutbox();
    if (getOfflineState().pending > 0) return;
  }
  const p = campaignPrefix(cid);
  const cursor = await idbGet<string>('meta', `cursor:${cid}`);
  let res: Response;
  try {
    res = await fetch(`${p}/sync${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`, { credentials: 'same-origin' });
  } catch {
    reportOffline();
    return;
  }
  if (isUnreachableStatus(res.status)) return reportOffline();
  if (!res.ok) return;
  reportOnline();
  setOfflineState({ syncing: true });
  try {
    const s = (await res.json()) as SyncResponse;
    await applySync(cid, s);
    await idbPut('meta', s.cursor, `cursor:${cid}`);
    const now = new Date().toISOString();
    await idbPut('meta', now, `lastSync:${cid}`);
    setOfflineState({ lastSync: { ...getOfflineState().lastSync, [cid]: now } });
    void cacheFiles(s);
  } finally {
    setOfflineState({ syncing: false });
  }
}

async function applySync(cid: string, s: SyncResponse) {
  const p = campaignPrefix(cid);
  const writes: { url: string; data: unknown }[] = [];
  const deletes: string[] = [];
  for (const n of s.notes) writes.push({ url: noteUrl(cid, n.id), data: n });
  for (const m of s.maps) writes.push({ url: mapUrl(cid, m.noteId), data: m });
  for (const c of s.combats) writes.push({ url: combatUrl(cid, c.id), data: c });
  for (const id of s.deletedNotes) deletes.push(noteUrl(cid, id));
  for (const id of s.deletedMaps) deletes.push(mapUrl(cid, id));
  for (const id of s.deletedCombats) deletes.push(combatUrl(cid, id));

  if (s.reset) {
    // Vollständiger Stand: lokal Übriggebliebenes (z. B. inzwischen gelöscht) entfernen
    const keep = new Set(writes.map((w) => w.url));
    for (const url of await cachedUrls(`${p}/`)) if (DETAIL.test(url) && !keep.has(url)) deletes.push(url);
  }

  // Notizen, die auf gelöschte Notizen verlinkten, haben jetzt offene Links
  const refetch = new Set<string>();
  if (s.deletedNotes.length > 0 && !s.reset) {
    const gone = new Set(s.deletedNotes);
    const changed = new Set(s.notes.map((n) => n.id));
    for (const url of await cachedUrls(`${p}/notes/`)) {
      if (!DETAIL.test(url)) continue;
      const n = await readCached<NoteDetail>(url);
      if (n && !changed.has(n.id) && Object.values(n.links).some((l) => l.type === 'note' && gone.has(l.id))) refetch.add(url);
    }
  }

  await writeManyCached(writes);
  await deleteCached(deletes);

  // Listen neu laden
  const lists = new Set<string>();
  const notesChanged = s.reset || s.notes.length > 0 || s.deletedNotes.length > 0;
  const player = (await readCached<CampaignDTO>(p))?.role === 'player';
  const allowed = (url: string) => !player || !GM_LISTS.has(url.slice(p.length).split('?')[0]!);
  if (s.reset) for (const l of BASE_LISTS) lists.add(p + l);
  if (notesChanged) {
    lists.add(`${p}/notes`);
    // Abgeleitete Listen sind teurer und ändern sich beim Tippen ständig: gebündelt nachziehen
    const derived = ['/graph', '/hierarchy', '/open-links', '/items'].map((l) => p + l).filter((u) => !player || !GM_LISTS.has(u.slice(p.length)));
    for (const url of await cachedUrls(`${p}/types/`)) if (url.endsWith('/notes')) derived.push(url);
    if (s.reset) derived.forEach((u) => lists.add(u));
    else refreshLater(cid, derived);
  }
  if (s.combats.length > 0 || s.deletedCombats.length > 0) lists.add(`${p}/combats`);
  for (const area of s.areas) {
    if (area === 'types') lists.add(`${p}/types`);
    else if (area === 'files') lists.add(`${p}/files`);
    else if (area === 'screen') lists.add(`${p}/screen`);
    else if (area === 'campaign') {
      lists.add(p);
      lists.add('/api/campaigns');
    } else if (area === 'catalog') {
      lists.add(`${p}/items`);
      for (const url of await cachedUrls(`${p}/catalog`)) lists.add(url);
      lists.add(`${p}/catalog`);
    }
  }
  if (s.reset) {
    lists.add(p);
    lists.add('/api/campaigns');
    lists.add('/api/auth/state');
  }
  await Promise.all([...lists, ...refetch].filter(allowed).map((url) => getJson(url)));

  invalidate(cid, s, notesChanged, refetch);
}

const LIST_DELAY_MS = 60_000;
const later = new Map<string, { urls: Set<string>; timer: number }>();

/** Listen höchstens einmal pro Minute neu laden (offen angezeigte Listen aktualisieren sich ohnehin selbst). */
function refreshLater(cid: string, urls: string[]) {
  let entry = later.get(cid);
  if (!entry) {
    const e = {
      urls: new Set<string>(),
      timer: window.setTimeout(() => {
        later.delete(cid);
        if (getOfflineState().online) void Promise.all([...e.urls].map((u) => getJson(u)));
      }, LIST_DELAY_MS),
    };
    later.set(cid, e);
    entry = e;
  }
  for (const u of urls) entry.urls.add(u);
}

/** Geöffnete Ansichten aktualisieren. Offene Editoren behalten ihren Entwurf (eigener Zustand). */
function invalidate(cid: string, s: SyncResponse, notesChanged: boolean, refetched: Set<string>) {
  const qc = queryClient;
  if (!qc) return;
  const inv = (...key: unknown[]) => void qc.invalidateQueries({ queryKey: key });
  if (s.reset) {
    for (const k of ['note', 'notes', 'graph', 'hierarchy', 'notesOfType', 'openLinks', 'items', 'map', 'combat', 'combats', 'types', 'files', 'catalog', 'screen', 'campaign']) inv(k, cid);
    return;
  }
  for (const n of s.notes) inv('note', cid, n.id);
  for (const url of refetched) inv('note', cid, url.split('/').pop());
  for (const id of s.deletedNotes) qc.removeQueries({ queryKey: ['note', cid, id] });
  if (notesChanged) for (const k of ['notes', 'graph', 'hierarchy', 'notesOfType', 'openLinks', 'items']) inv(k, cid);
  for (const m of s.maps) inv('map', cid, m.noteId);
  for (const c of s.combats) inv('combat', cid, c.id);
  if (s.combats.length || s.deletedCombats.length) inv('combats', cid);
  for (const area of s.areas) {
    if (area === 'types') inv('types', cid);
    else if (area === 'files') inv('files', cid);
    else if (area === 'screen') inv('screen', cid);
    else if (area === 'catalog') {
      inv('catalog', cid);
      inv('items', cid);
    } else if (area === 'campaign') {
      inv('campaign', cid);
      inv('campaigns');
    }
  }
}

const FILE_URL = /\/api\/files\/[0-9a-f-]{36}\?v=[0-9a-f]+/gi;
export const FILE_CACHE = 'kt-files';

/** Bilder (Karten, Tokens, eingebettete Bilder) für offline vorhalten. */
async function cacheFiles(s: SyncResponse) {
  if (typeof caches === 'undefined') return;
  const urls = new Set(JSON.stringify(s).match(FILE_URL) ?? []);
  if (urls.size === 0) return;
  try {
    const cache = await caches.open(FILE_CACHE);
    for (const url of urls) {
      if (await cache.match(url)) continue;
      try {
        await cache.add(url);
      } catch {
        /* einzelnes Bild nicht erreichbar */
      }
    }
  } catch {
    /* Cache-API nicht verfügbar */
  }
}

/** Stand der lokalen Kopie (Einstellungen). */
export async function localCopyInfo(cid: string): Promise<{ notes: number; maps: number; combats: number; files: number; lastSync: string | null }> {
  const p = campaignPrefix(cid);
  const urls = (await cachedUrls(`${p}/`)).filter((u) => DETAIL.test(u));
  let files = 0;
  if (typeof caches !== 'undefined') {
    try {
      files = (await (await caches.open(FILE_CACHE)).keys()).length;
    } catch {
      /* egal */
    }
  }
  return {
    notes: urls.filter((u) => u.includes('/notes/')).length,
    maps: urls.filter((u) => u.includes('/maps/')).length,
    combats: urls.filter((u) => u.includes('/combats/')).length,
    files,
    lastSync: (await idbGet<string>('meta', `lastSync:${cid}`)) ?? null,
  };
}

/** Lokale Kopie einer Kampagne verwerfen und neu laden. */
export async function resetLocalCopy(cid: string): Promise<void> {
  await idbPut('meta', null, `cursor:${cid}`);
  await pullCampaign(cid);
}
